import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bot, MessageCircle, Eye, EyeOff, CheckCircle, X, Wallet, Heart, BarChart2, Settings, ToggleRight, Search, Map, ShieldCheck, FileText, Trophy, Save, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { isSoftLaunchEnabled, setSoftLaunchEnabled } from "@/data/softLaunchStore";
import { isGlobalDemoModeEnabled, setGlobalDemoModeEnabled } from "@/data/demoStore";
import { getExportWatermarkEnabled, setExportWatermarkEnabled } from "@/utils/exportUtils";
import { getCmsLayout, publishCmsLayout } from "@/data/cmsStore";
import { getPostersConfig, savePostersConfig } from "@/data/postersStore";
import { getStrictDelegateMode, setStrictDelegateMode } from "@/data/partiesStore";
import { useMatchStore } from "@/data/matchStore";
import { useRingStore } from "@/data/ringStore";
import { getBarometerConfig, saveBarometerConfig } from "@/utils/barometerConfigStore";
import { useSettingsStore } from "@/data/settingsStore";
import { usePartnerAccessStore } from "@/data/partnerAccessStore";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/utils/apiConfig";
import { RichTextarea } from "@/components/ui/RichTextarea";

export interface AdminQuickSettingsProps {
  chatbotOn: boolean;
  toggleChatbot: () => void;
  stagingModeOn: boolean;
  toggleStagingMode: () => void;
  commercialModeOn: boolean;
  toggleCommercialMode: () => void;
  partyLikesOn: boolean;
  togglePartyLikes: () => void;
  eventLikesOn: boolean;
  toggleEventLikes: () => void;
  barometerOn: boolean;
  toggleBarometer: () => void;
}

export function AdminQuickSettingsContent({
  chatbotOn, toggleChatbot,
  stagingModeOn, toggleStagingMode,
  commercialModeOn, toggleCommercialMode,
  partyLikesOn, togglePartyLikes,
  eventLikesOn, toggleEventLikes,
  barometerOn, toggleBarometer,
}: AdminQuickSettingsProps) {
  const { toast } = useToast();

  const [softLaunch, setSoftLaunch] = useState(isSoftLaunchEnabled());
  const [demoMode, setDemoMode] = useState(isGlobalDemoModeEnabled());
  const [watermark, setWatermark] = useState(getExportWatermarkEnabled());
  const [strictMode, setStrictMode] = useState(getStrictDelegateMode());
  const [postersCfg, setPostersCfg] = useState(getPostersConfig());
  
  const { isMatchEnabled, toggleMatchEnabled } = useMatchStore();
  const { isRingResultsEnabled, toggleRingResultsEnabled } = useRingStore();
  
  const { settings: partnerSettings, setRestrictionEnabled, setPartyLimit, setMediaLimit, setOngLimit, setInstitutionLimit } = usePartnerAccessStore();

  const [cmsHeroHideSearch, setCmsHeroHideSearch] = useState(false);
  const [cmsHeroDisableSearch, setCmsHeroDisableSearch] = useState(false);
  const [cmsMapVisible, setCmsMapVisible] = useState(true);
  
  const [barometerPending, setBarometerPending] = useState(getBarometerConfig().status === "pending");

  const { updateSetting } = useSettingsStore();
  const settings = useSettingsStore(state => state.settings);
  
  const getSettingReactive = (key: string, fallback: string | boolean) => settings[key] !== undefined ? settings[key] : fallback;

  const [siteTitle, setSiteTitle] = useState(getSettingReactive("kaynin_site_title", "Kayn'in_ⴽⴰⵢⵏⵉⵏ_كاينين") as string);
  const [siteDescription, setSiteDescription] = useState(getSettingReactive("kaynin_site_description", "Plateforme citoyenne nouvelle génération pour une démocratie participative active et un engagement citoyen au Maroc. منصة مواطنة من الجيل الجديد لديمقراطية تشاركية فاعلة وإلتزام مواطن في المغرب") as string);
  const [hideExpectationsCount, setHideExpectationsCount] = useState(getSettingReactive("kaynin_hide_expectations_count", "false") === "true");
  const [contactEmail, setContactEmail] = useState(getSettingReactive("kaynin_contact_email", "contact@kaynin.ma") as string);
  const [footerBannerVisible, setFooterBannerVisible] = useState(getSettingReactive("kaynin_footer_banner_visible", "false") === "true");
  const [footerBannerContent, setFooterBannerContent] = useState(getSettingReactive("kaynin_footer_banner_content", "") as string);
  const [footerBannerContentAr, setFooterBannerContentAr] = useState(getSettingReactive("kaynin_footer_banner_content_ar", "") as string);
  const [footerBannerContentZgh, setFooterBannerContentZgh] = useState(getSettingReactive("kaynin_footer_banner_content_zgh", "") as string);
  const [footerBannerBg, setFooterBannerBg] = useState(getSettingReactive("kaynin_footer_banner_bg", "bg-[#0a1f18]") as string);
  const [scraperEnabled, setScraperEnabled] = useState(getSettingReactive("scraper_enabled", "false") === "true");
  const [scraperCronTime, setScraperCronTime] = useState(getSettingReactive("scraper_cron_time", "03:00") as string);
  const [isScraping, setIsScraping] = useState(false);
  const [scraperSources, setScraperSources] = useState<any[]>([]);
  const [newSourceName, setNewSourceName] = useState('');
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [newSourceType, setNewSourceType] = useState('facebook');

  const fetchScraperSources = async () => {
    try {
      const res = await fetch(`${getApiUrl()}/scraper_sources.php`);
      const data = await res.json();
      if (data.success) {
        setScraperSources(data.sources);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchScraperSources();
  }, []);

  useEffect(() => {
    setSiteTitle(getSettingReactive("kaynin_site_title", "Kayn'in_ⴽⴰⵢⵏⵉⵏ_كاينين") as string);
    setSiteDescription(getSettingReactive("kaynin_site_description", "Plateforme citoyenne nouvelle génération pour une démocratie participative active et un engagement citoyen au Maroc. منصة مواطنة من الجيل الجديد لديمقراطية تشاركية فاعلة وإلتزام مواطن في المغرب") as string);
    setHideExpectationsCount(getSettingReactive("kaynin_hide_expectations_count", "false") === "true");
    setContactEmail(getSettingReactive("kaynin_contact_email", "contact@kaynin.ma") as string);
    setFooterBannerVisible(getSettingReactive("kaynin_footer_banner_visible", "false") === "true");
    setFooterBannerContent(getSettingReactive("kaynin_footer_banner_content", "") as string);
    setFooterBannerContentAr(getSettingReactive("kaynin_footer_banner_content_ar", "") as string);
    setFooterBannerContentZgh(getSettingReactive("kaynin_footer_banner_content_zgh", "") as string);
    setFooterBannerBg(getSettingReactive("kaynin_footer_banner_bg", "bg-[#0a1f18]") as string);
    setScraperEnabled(getSettingReactive("scraper_enabled", "false") === "true");
    setScraperCronTime(getSettingReactive("scraper_cron_time", "03:00") as string);
  }, [settings]);

  // Chatbot settings
  const getParsedSetting = (key: string, def: string[]) => {
    try {
      const val = getSettingReactive(key, JSON.stringify(def));
      return typeof val === 'string' ? JSON.parse(val) : val;
    } catch { return def; }
  };
  
  const [chatbotPaths, setChatbotPaths] = useState<string[]>(getParsedSetting("kaynin_chatbot_paths", ["/"]));
  
  // Gamification badge settings
  const [badgeEnabled, setBadgeEnabled] = useState(getSettingReactive("kaynin_badge_enabled", "true") !== "false");
  const [badgePosition, setBadgePosition] = useState(getSettingReactive("kaynin_badge_position", "left") as string);
  const [badgePaths, setBadgePaths] = useState<string[]>(getParsedSetting("kaynin_badge_paths", ["/"]));

  useEffect(() => {
    setChatbotPaths(getParsedSetting("kaynin_chatbot_paths", ["/"]));
    setBadgeEnabled(getSettingReactive("kaynin_badge_enabled", "true") !== "false");
    setBadgePosition(getSettingReactive("kaynin_badge_position", "left") as string);
    setBadgePaths(getParsedSetting("kaynin_badge_paths", ["/"]));
  }, [settings]);

  const AVAILABLE_PATHS = [
    { path: "/", label: "Accueil" },
    { path: "/voter", label: "Électeur" },
    { path: "/partis", label: "Partis" },
    { path: "/match", label: "Match" },
    { path: "/ring", label: "Le Ring" },
  ];

  const handlePublishChoices = () => {
    updateSetting("kaynin_chatbot_paths", JSON.stringify(chatbotPaths));
    updateSetting("kaynin_badge_enabled", badgeEnabled ? "true" : "false");
    updateSetting("kaynin_badge_position", badgePosition);
    updateSetting("kaynin_badge_paths", JSON.stringify(badgePaths));
    toast({
      title: "Choix publiés",
      description: "Les paramètres d'affichage pour le Chatbot et le Badge ont été enregistrés.",
    });
  };

  useEffect(() => {
    const layout = getCmsLayout('home');
    const hero = layout.find(s => s.id === 'hero');
    const map = layout.find(s => s.id === 'map');
    
    if (hero) {
      setCmsHeroHideSearch(hero.content.hero_hide_search_bar === "true" || hero.content.hero_hide_search_bar === true);
      setCmsHeroDisableSearch(hero.content.hero_disable_search === "true" || hero.content.hero_disable_search === true);
    }
    if (map) {
      setCmsMapVisible(map.visible);
    }
  }, []);

  const handleCmsChange = (sectionId: string, property: string, value: any, isVisibleToggle: boolean = false) => {
    const layout = getCmsLayout('home');
    const updated = layout.map(s => {
      if (s.id === sectionId) {
        if (isVisibleToggle) {
          return { ...s, visible: value };
        }
        return { ...s, content: { ...s.content, [property]: String(value) } };
      }
      return s;
    });
    publishCmsLayout('home', updated);
    
    if (property === 'hero_hide_search_bar') setCmsHeroHideSearch(value);
    if (property === 'hero_disable_search') setCmsHeroDisableSearch(value);
    if (isVisibleToggle && sectionId === 'map') setCmsMapVisible(value);
  };

  const updatePosterCfg = (k: keyof typeof postersCfg, v: boolean) => {
    const next = { ...postersCfg, [k]: v };
    setPostersCfg(next);
    savePostersConfig(next);
  };

  const handleSoftLaunch = (v: boolean) => {
    setSoftLaunch(v);
    setSoftLaunchEnabled(v);
    toast({ title: "Accès mis à jour", description: v ? "Lancement Progressif activé" : "Plateforme Publique" });
  };

  const handleDemoMode = async (v: boolean) => {
    setDemoMode(v);
    setGlobalDemoModeEnabled(v);
    
    if (!v) {
      if (confirm("Vous passez en Mode Production. Voulez-vous purger toutes les données fictives (comptes tests, délégués, candidats, événements, émissions) de la base de données ?")) {
         try {
            const token = localStorage.getItem('kaynin_auth_token') || "";
            const res = await fetch(`${getApiUrl()}/purge_mock_data.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
            });
            if (res.ok) {
               toast({ title: "Mode Production", description: "Toutes les données fictives ont été purgées avec succès." });
            } else {
               toast({ title: "Erreur", description: "Échec de la purge des données fictives.", variant: "destructive" });
            }
         } catch (e) {
            console.error(e);
            toast({ title: "Erreur", description: "Impossible de contacter le serveur pour la purge.", variant: "destructive" });
         }
      } else {
         toast({ title: "Mode Production", description: "Mode de production activé sans purger la base." });
      }
    } else {
      toast({ title: "Mode de données", description: "Mode Démo activé." });
    }
  };

  const handleWatermark = (v: boolean) => {
    setWatermark(v);
    setExportWatermarkEnabled(v);
  };

  const handleStrictMode = (v: boolean) => {
    setStrictMode(v);
    setStrictDelegateMode(v);
  };

  const handleBarometerPending = (v: boolean) => {
    setBarometerPending(v);
    const config = getBarometerConfig();
    saveBarometerConfig({ ...config, status: v ? "pending" : "active" });
  };

  const handleManualScrape = async () => {
    setIsScraping(true);
    try {
      const res = await fetch(`${getApiUrl()}/cron_scheduler.php?token=CRON_KAYNIN_2026&force=1`);
      const data = await res.text();
      toast({ title: "Scraper", description: data });
    } catch(err: any) {
      toast({ title: "Erreur", description: "Impossible de lancer le scraper.", variant: "destructive" });
    } finally {
      setIsScraping(false);
    }
  };

  const handleAddSource = async () => {
    if (!newSourceName || !newSourceUrl || !newSourceType) {
      toast({ title: "Erreur", description: "Veuillez remplir tous les champs de la source.", variant: "destructive" });
      return;
    }
    try {
      const res = await fetch(`${getApiUrl()}/scraper_sources.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newSourceName, url: newSourceUrl, type: newSourceType })
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: "Source ajoutée", description: data.message });
        setNewSourceName('');
        setNewSourceUrl('');
        fetchScraperSources();
      } else {
        toast({ title: "Erreur", description: data.error, variant: "destructive" });
      }
    } catch (e) {
      toast({ title: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleToggleSource = async (id: string, currentStatus: number) => {
    try {
      const res = await fetch(`${getApiUrl()}/scraper_sources.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, is_active: currentStatus === 1 ? 0 : 1 })
      });
      if (res.ok) fetchScraperSources();
    } catch (e) {}
  };

  const handleDeleteSource = async (id: string) => {
    if (!confirm("Voulez-vous supprimer cette source ?")) return;
    try {
      const res = await fetch(`${getApiUrl()}/scraper_sources.php?id=${id}`, {
        method: 'DELETE'
      });
      if (res.ok) fetchScraperSources();
    } catch (e) {}
  };

  return (
    <div className="pb-8 overflow-y-auto">
      <Tabs defaultValue="system" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-1 h-auto bg-muted/50 p-1 rounded-xl mb-6">
          <TabsTrigger value="system" className="text-xs font-semibold rounded-lg">🚀 Système</TabsTrigger>
          <TabsTrigger value="ui" className="text-xs font-semibold rounded-lg">🎨 Interface</TabsTrigger>
          <TabsTrigger value="parties" className="text-xs font-semibold rounded-lg">🗳️ Partis</TabsTrigger>
          <TabsTrigger value="tools" className="text-xs font-semibold rounded-lg">🛠️ Outils</TabsTrigger>
        </TabsList>

        <TabsContent value="system" className="space-y-4">
          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-blue-700">Lancement Progressif (Soft Launch)</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Restreint l'accès au site avec une page "Bientôt disponible". Seuls les partenaires peuvent se connecter. <br/>
                <strong>Mots-clés: B2B, Privé, Verrouillé</strong>
              </p>
            </div>
            <Switch checked={softLaunch} onCheckedChange={handleSoftLaunch} className="data-[state=checked]:bg-blue-600 shrink-0" />
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-amber-600">Mode Démo Global</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Remplace les vraies statistiques par des fausses données visuelles pour les démonstrations aux clients.<br/>
                <strong>Mots-clés: Faux chiffres, Démo, Tests</strong>
              </p>
            </div>
            <Switch checked={demoMode} onCheckedChange={handleDemoMode} className="data-[state=checked]:bg-amber-500 shrink-0" />
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-purple-700">Aperçu Brouillons (Staging)</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Active la prévisualisation de vos modifications non publiées (CMS) uniquement pour votre session d'administration.<br/>
                <strong>Mots-clés: Prévisualisation, CMS, Drafts</strong>
              </p>
            </div>
            <Switch checked={stagingModeOn} onCheckedChange={toggleStagingMode} className="data-[state=checked]:bg-purple-600 shrink-0" />
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-slate-700">Filigrane Exports</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Ajoute automatiquement le logo "Kayn'in" sur tous les documents PDF et images exportés.<br/>
                <strong>Mots-clés: Export, PDF, Copyright</strong>
              </p>
            </div>
            <Switch checked={watermark} onCheckedChange={handleWatermark} className="shrink-0" />
          </div>
          
          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-slate-700">Mode Commercial (Finances)</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Affiche l'onglet "Finances" dans l'administration pour suivre les factures des partis.<br/>
                <strong>Mots-clés: Monétisation, Factures, B2B</strong>
              </p>
            </div>
            <Switch checked={commercialModeOn} onCheckedChange={toggleCommercialMode} className="shrink-0" />
          </div>

          <div className="bg-red-50 p-4 rounded-xl border border-red-200 shadow-sm flex flex-col gap-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="space-y-1 w-full md:max-w-[70%]">
                <Label className="text-base font-bold text-red-700">Restreindre les accès Partenaires</Label>
                <p className="text-xs text-muted-foreground leading-snug">
                  Active le workflow de "Délégué Principal" et "Demandes d'accès supplémentaires". Limite le nombre de comptes par entité.<br/>
                  <strong>Mots-clés: Sécurité, Accréditations, Limites</strong>
                </p>
              </div>
              <Switch checked={partnerSettings.isRestrictionEnabled} onCheckedChange={setRestrictionEnabled} className="data-[state=checked]:bg-red-600 shrink-0" />
            </div>
            
            {partnerSettings.isRestrictionEnabled && (
              <div className="space-y-4 mt-2 pt-4 border-t border-red-200/50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-red-900">Limite Partis</Label>
                    <input type="number" min="1" value={partnerSettings.partyLimit} onChange={(e) => setPartyLimit(Number(e.target.value))} className="w-full rounded-md border p-2 text-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-red-900">Limite Médias</Label>
                    <input type="number" min="1" value={partnerSettings.mediaLimit} onChange={(e) => setMediaLimit(Number(e.target.value))} className="w-full rounded-md border p-2 text-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-red-900">Limite ONGs</Label>
                    <input type="number" min="1" value={partnerSettings.ongLimit} onChange={(e) => setOngLimit(Number(e.target.value))} className="w-full rounded-md border p-2 text-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-red-900">Limite Institutions</Label>
                    <input type="number" min="1" value={partnerSettings.institutionLimit} onChange={(e) => setInstitutionLimit(Number(e.target.value))} className="w-full rounded-md border p-2 text-sm" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" variant="destructive" onClick={() => toast({ title: "Limites sauvegardées", description: "Les nouvelles limites d'accès ont été enregistrées avec succès." })}>
                    Sauvegarder les limites
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ui" className="space-y-4">
          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[50%]">
              <Label className="text-base font-bold text-slate-700">Titre global du site (Navigateur)</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Ce titre s'affiche dans l'onglet du navigateur et lors des partages (SEO). Par défaut : <em>Kayn'in_ⴽⴰⵢⵏⵉⵏ_كاينين</em><br/>
                <strong>Mots-clés: SEO, Titre, Onglet</strong>
              </p>
            </div>
            <div className="flex gap-2 w-full md:max-w-[45%]">
              <input 
                type="text" 
                value={siteTitle} 
                onChange={(e) => setSiteTitle(e.target.value)} 
                className="flex-1 rounded-md border p-2 text-sm" 
              />
              <Button size="sm" onClick={() => { updateSetting("kaynin_site_title", siteTitle); toast({ title: "Titre mis à jour", description: "Le titre du site a été modifié avec succès." }); }}>
                Sauvegarder
              </Button>
            </div>
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col gap-4 items-start justify-between">
            <div className="space-y-1 w-full">
              <Label className="text-base font-bold text-slate-700">Description globale du site (SEO & Réseaux Sociaux)</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Cette description courte est lue par Google et s'affiche sous le lien lorsque partagé sur Facebook, WhatsApp, etc.<br/>
                <strong>Mots-clés: SEO, Description, Google, Partage</strong>
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-2 w-full">
              <textarea 
                value={siteDescription} 
                onChange={(e) => setSiteDescription(e.target.value)} 
                className="flex-1 rounded-md border p-2 text-sm min-h-[80px]" 
              />
              <Button size="sm" className="whitespace-nowrap" onClick={() => { updateSetting("kaynin_site_description", siteDescription); toast({ title: "Description mise à jour", description: "La description SEO a été modifiée avec succès." }); }}>
                Sauvegarder
              </Button>
            </div>
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col gap-4 items-start justify-between">
            <div className="space-y-1 w-full">
              <Label className="text-base font-bold text-slate-700">Email du formulaire de contact</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                C'est l'adresse email vers laquelle les messages du formulaire "Nous contacter" seront redirigés.<br/>
                <strong>Mots-clés: Contact, Email, Formulaire</strong>
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-2 w-full">
              <input 
                type="email" 
                value={contactEmail} 
                onChange={(e) => setContactEmail(e.target.value)} 
                className="flex-1 rounded-md border p-2 text-sm" 
              />
              <Button size="sm" className="whitespace-nowrap" onClick={() => { updateSetting("kaynin_contact_email", contactEmail); toast({ title: "Email mis à jour", description: "L'email de contact a été modifié avec succès." }); }}>
                Sauvegarder
              </Button>
            </div>
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-slate-700">Masquer Barre de Recherche Hero</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Fait disparaître complètement la grosse barre de recherche centrale sur la toute première page du site.<br/>
                <strong>Mots-clés: Accueil, Recherche, Invisible</strong>
              </p>
            </div>
            <Switch checked={cmsHeroHideSearch} onCheckedChange={(v) => handleCmsChange('hero', 'hero_hide_search_bar', v)} className="shrink-0" />
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-slate-700">Verrouiller la Recherche ("En cours...")</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                La barre de recherche reste visible mais est cliquée/grisée et affiche "En cours de consolidation...". Utile en phase de préparation.<br/>
                <strong>Mots-clés: Accueil, Recherche, Désactivé</strong>
              </p>
            </div>
            <Switch checked={cmsHeroDisableSearch} onCheckedChange={(v) => handleCmsChange('hero', 'hero_disable_search', v)} className="shrink-0" />
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-slate-700">Carte Électorale Maroc</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Active ou désactive la grande carte interactive du Maroc sur la page d'accueil.<br/>
                <strong>Mots-clés: Carte, Régions, Accueil</strong>
              </p>
            </div>
            <Switch checked={cmsMapVisible} onCheckedChange={(v) => handleCmsChange('map', '', v, true)} className="shrink-0" />
          </div>

          <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-amber-700">Masquer Baromètre (En Accueil)</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Fait disparaître complètement le bloc "Baromètre en direct" sur la page d'accueil.<br/>
                <strong>Mots-clés: Accueil, Stats, Invisible</strong>
              </p>
            </div>
            <Switch checked={!barometerOn} onCheckedChange={(v) => toggleBarometer()} className="shrink-0" />
          </div>

          <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-amber-700">Verrouiller Baromètre ("En cours...")</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Laisse le baromètre visible mais désactive les onglets en affichant un gros message "En cours de consolidation...".<br/>
                <strong>Mots-clés: Accueil, Stats, Désactivé</strong>
              </p>
            </div>
            <Switch checked={barometerPending} onCheckedChange={handleBarometerPending} className="shrink-0" />
          </div>

          <div className="bg-rose-50 p-4 rounded-xl border border-rose-200 shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-rose-700">Match Ton Parti</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Active l'outil "Tinder Politique" permettant aux citoyens de matcher avec un parti.<br/>
                <strong>Mots-clés: Quiz, Match, Outil public</strong>
              </p>
            </div>
            <Switch checked={isMatchEnabled} onCheckedChange={toggleMatchEnabled} className="data-[state=checked]:bg-rose-500 shrink-0" />
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-slate-700">Masquer le compteur "Attentes Citoyennes"</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Cache le nombre de répondants (ex: "2 مجيب") sous le titre "Attentes et Priorités" jusqu'à ce qu'un nombre raisonnable soit atteint.<br/>
                <strong>Mots-clés: Compteur, Répondants, Attentes</strong>
              </p>
            </div>
            <Switch 
              checked={hideExpectationsCount} 
              onCheckedChange={(v) => {
                setHideExpectationsCount(v);
                updateSetting("kaynin_hide_expectations_count", String(v));
              }} 
              className="shrink-0" 
            />
          </div>

          <div className="bg-card p-4 rounded-xl border border-primary/20 shadow-sm flex flex-col gap-4 items-start justify-between">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-4">
              <div className="space-y-1 w-full md:max-w-[70%]">
                <Label className="text-base font-bold text-primary">Bandeau d'Annonce (Avant Footer)</Label>
                <p className="text-xs text-muted-foreground leading-snug">
                  Affiche un bandeau d'annonce entre le dernier bloc vert et le footer noir. Utilisez l'éditeur pour personnaliser le message.<br/>
                  <strong>Mots-clés: Annonce, Footer, Bandeau</strong>
                </p>
              </div>
              <Switch 
                checked={footerBannerVisible} 
                onCheckedChange={(v) => {
                  setFooterBannerVisible(v);
                  updateSetting("kaynin_footer_banner_visible", String(v));
                }} 
                className="shrink-0" 
              />
            </div>
            
            {footerBannerVisible && (
              <div className="w-full space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Couleur de fond du bandeau</Label>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setFooterBannerBg("bg-[#0a1f18]")} 
                      className={`w-8 h-8 rounded-full bg-[#0a1f18] border-2 transition-transform ${footerBannerBg === "bg-[#0a1f18]" ? "border-primary scale-110" : "border-transparent"}`}
                      title="Vert très foncé (Recommandé pour transition)"
                    />
                    <button 
                      onClick={() => setFooterBannerBg("bg-black")} 
                      className={`w-8 h-8 rounded-full bg-black border-2 transition-transform ${footerBannerBg === "bg-black" ? "border-primary scale-110" : "border-transparent"}`}
                      title="Noir"
                    />
                    <button 
                      onClick={() => setFooterBannerBg("bg-primary")} 
                      className={`w-8 h-8 rounded-full bg-primary border-2 transition-transform ${footerBannerBg === "bg-primary" ? "border-white scale-110" : "border-transparent"}`}
                      title="Vert Kayn'in"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Contenu de l'annonce (Français)</Label>
                    <RichTextarea 
                      value={footerBannerContent} 
                      onChange={(e) => setFooterBannerContent(e.target.value)} 
                      className="min-h-[120px] bg-background" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Contenu de l'annonce (Arabe)</Label>
                    <RichTextarea 
                      value={footerBannerContentAr} 
                      onChange={(e) => setFooterBannerContentAr(e.target.value)} 
                      className="min-h-[120px] bg-background" 
                      dir="rtl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Contenu de l'annonce (Amazigh)</Label>
                    <RichTextarea 
                      value={footerBannerContentZgh} 
                      onChange={(e) => setFooterBannerContentZgh(e.target.value)} 
                      className="min-h-[120px] bg-background" 
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button 
                    size="sm" 
                    onClick={() => { 
                      updateSetting("kaynin_footer_banner_content", footerBannerContent); 
                      updateSetting("kaynin_footer_banner_content_ar", footerBannerContentAr);
                      updateSetting("kaynin_footer_banner_content_zgh", footerBannerContentZgh);
                      updateSetting("kaynin_footer_banner_bg", footerBannerBg);
                      toast({ title: "Bandeau mis à jour", description: "Le contenu et la couleur du bandeau ont été enregistrés." }); 
                    }}
                  >
                    Sauvegarder le contenu
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="parties" className="space-y-4">
          <div className="bg-red-50 p-4 rounded-xl border border-red-200 shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-red-700">Masquer toutes les affiches</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Cache complètement les cartes des partis sur l'accueil. <em>Prioritaire sur tout le reste.</em><br/>
                <strong>Mots-clés: Cacher tout, Invisible, Danger</strong>
              </p>
            </div>
            <Switch checked={postersCfg.disableAll} onCheckedChange={(v) => updatePosterCfg('disableAll', v)} className="data-[state=checked]:bg-red-500 shrink-0" />
          </div>

          <div className="bg-orange-50 p-4 rounded-xl border border-orange-200 shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-orange-700">Verrouiller les affiches</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Garde les affiches visibles mais désactive tous les liens cliquables et met les sièges à 0.<br/>
                <strong>Mots-clés: Non cliquable, Verrouillé, 0 sièges</strong>
              </p>
            </div>
            <Switch checked={postersCfg.lockPosters} onCheckedChange={(v) => updatePosterCfg('lockPosters', v)} disabled={postersCfg.disableAll} className="data-[state=checked]:bg-orange-500 shrink-0" />
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-slate-700">Afficher Sièges Actuels</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Montre le nombre de sièges parlementaires. Si décoché, met à zéro.<br/>
                <strong>Mots-clés: Chiffres, Parlement</strong>
              </p>
            </div>
            <Switch checked={postersCfg.showSeats} onCheckedChange={(v) => updatePosterCfg('showSeats', v)} disabled={postersCfg.disableAll || postersCfg.lockPosters} className="shrink-0" />
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-slate-700">Liens Affiches (Site, Prog, Profil)</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Active les clics vers le profil interne du parti, son programme et son site externe.<br/>
                <strong>Mots-clés: Clics, Liens sortants</strong>
              </p>
            </div>
            <Switch checked={postersCfg.showPartyLink} onCheckedChange={(v) => updatePosterCfg('showPartyLink', v)} disabled={postersCfg.disableAll || postersCfg.lockPosters} className="shrink-0" />
          </div>

          <div className="bg-orange-50 p-4 rounded-xl border border-orange-200 shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mt-6">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-orange-700">Forcer le Silence Électoral</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Met instantanément les sièges à 0 et coupe toutes les pubs pour simuler le silence légal.<br/>
                <strong>Mots-clés: Loi, Légal, Simulation</strong>
              </p>
            </div>
            <Switch checked={postersCfg.forceSilenceElectoral} onCheckedChange={(v) => updatePosterCfg('forceSilenceElectoral', v)} className="data-[state=checked]:bg-orange-500 shrink-0" />
          </div>

          <div className="bg-card p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mt-2">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-slate-700">Code Strict (Délégués)</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Exige le vrai code d'authentification pour accéder à l'espace Délégués des partis (Sécurité).<br/>
                <strong>Mots-clés: Sécurité, Login partis</strong>
              </p>
            </div>
            <Switch checked={strictMode} onCheckedChange={handleStrictMode} className="shrink-0" />
          </div>
        </TabsContent>

        <TabsContent value="tools" className="space-y-4">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="space-y-1 w-full md:max-w-[70%]">
                <Label className="text-base font-bold text-slate-700">Aspirateur Automatique (Scraper Facebook)</Label>
                <p className="text-xs text-muted-foreground leading-snug">
                  Robot qui extrait automatiquement les publications de partis politiques (ex: PPS) sur Facebook pour créer des brouillons.<br/>
                  <strong>Mots-clés: Bot, Scraping, Veille, Facebook</strong>
                </p>
              </div>
              <Switch checked={scraperEnabled} onCheckedChange={(v) => { setScraperEnabled(v); updateSetting("scraper_enabled", String(v)); }} className="data-[state=checked]:bg-slate-600 shrink-0" />
            </div>

            <div className="mt-2 pt-3 border-t border-slate-200/50 space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-900">Heure du déclenchement automatique (Cron)</Label>
                <div className="flex gap-2">
                  <input type="time" value={scraperCronTime} onChange={(e) => setScraperCronTime(e.target.value)} className="rounded-md border p-2 text-sm" />
                  <Button size="sm" onClick={() => { updateSetting("scraper_cron_time", scraperCronTime); toast({ title: "Heure sauvegardée" }); }}>Sauvegarder</Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-900">Sources à aspirer</Label>
                <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                  <div className="flex gap-2 p-2 bg-slate-100 border-b items-center">
                    <Input placeholder="Nom (ex: PAM Officiel)" value={newSourceName} onChange={e => setNewSourceName(e.target.value)} className="h-8 text-xs flex-1" />
                    <Input placeholder="URL (ex: https://facebook.com/...)" value={newSourceUrl} onChange={e => setNewSourceUrl(e.target.value)} className="h-8 text-xs flex-[2]" />
                    <Select value={newSourceType} onValueChange={setNewSourceType}>
                      <SelectTrigger className="h-8 w-32 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="website">Site Web (Générique)</SelectItem>
                        <SelectItem value="twitter">Twitter / X</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8 px-3 gap-1 text-xs" onClick={handleAddSource}>
                      <Plus className="w-3 h-3" /> Ajouter
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {scraperSources.length === 0 ? (
                      <div className="p-4 text-center text-xs text-muted-foreground">Aucune source configurée.</div>
                    ) : (
                      <table className="w-full text-xs text-left">
                        <thead className="bg-slate-50 sticky top-0 border-b">
                          <tr>
                            <th className="px-3 py-2">Nom</th>
                            <th className="px-3 py-2">Type</th>
                            <th className="px-3 py-2">URL</th>
                            <th className="px-3 py-2 text-center">Actif</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {scraperSources.map(src => (
                            <tr key={src.id} className="hover:bg-slate-50">
                              <td className="px-3 py-2 font-medium">{src.name}</td>
                              <td className="px-3 py-2"><span className="bg-slate-100 px-2 py-0.5 rounded text-[10px] uppercase font-bold">{src.type}</span></td>
                              <td className="px-3 py-2 truncate max-w-[200px]" title={src.url}>{src.url}</td>
                              <td className="px-3 py-2 text-center">
                                <Switch checked={src.is_active == 1} onCheckedChange={() => handleToggleSource(src.id, src.is_active)} className="scale-75" />
                              </td>
                              <td className="px-3 py-2 text-right">
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:bg-red-50" onClick={() => handleDeleteSource(src.id)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-900">Déclenchement Manuel</Label>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={handleManualScrape} disabled={isScraping || !scraperEnabled}>
                    {isScraping ? "Aspiration en cours..." : "Lancer le Scraper maintenant"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 shadow-sm flex flex-col gap-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="space-y-1 w-full md:max-w-[70%]">
                <Label className="text-base font-bold text-emerald-700">Kayn'Bot (IA)</Label>
                <p className="text-xs text-muted-foreground leading-snug">
                  Active le robot conversationnel pour aider les citoyens.<br/>
                  <strong>Mots-clés: Chatbot, Intelligence Artificielle</strong>
                </p>
              </div>
              <Switch checked={chatbotOn} onCheckedChange={toggleChatbot} className="data-[state=checked]:bg-emerald-500 shrink-0" />
            </div>
            
            <div className="mt-2 pt-3 border-t border-emerald-200/50 space-y-2">
              <Label className="text-xs font-semibold text-emerald-900">Emplacements d'affichage du Chatbot</Label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_PATHS.map(item => (
                  <label key={`cb-${item.path}`} className="flex items-center gap-1.5 cursor-pointer bg-white/60 px-2 py-1 rounded text-xs">
                    <input 
                      type="checkbox" 
                      checked={chatbotPaths.includes(item.path)}
                      onChange={(e) => setChatbotPaths(e.target.checked ? [...chatbotPaths, item.path] : chatbotPaths.filter(p => p !== item.path))}
                      className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    {item.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200 shadow-sm flex flex-col gap-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="space-y-1 w-full md:max-w-[70%]">
                <Label className="text-base font-bold text-indigo-700">Badge Gamification (Citoyen)</Label>
                <p className="text-xs text-muted-foreground leading-snug">
                  Affiche le widget d'XP et de niveau pour les citoyens connectés.<br/>
                  <strong>Mots-clés: XP, Gamification, Badges</strong>
                </p>
              </div>
              <Switch checked={badgeEnabled} onCheckedChange={setBadgeEnabled} className="data-[state=checked]:bg-indigo-600 shrink-0" />
            </div>

            <div className="mt-2 pt-3 border-t border-indigo-200/50 space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-indigo-900">Position sur l'écran</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" name="badgePosition" value="left" checked={badgePosition === "left"} onChange={() => setBadgePosition("left")} /> En bas à Gauche
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" name="badgePosition" value="right" checked={badgePosition === "right"} onChange={() => setBadgePosition("right")} /> En bas à Droite
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-indigo-900">Emplacements d'affichage du Badge</Label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_PATHS.map(item => (
                    <label key={`badge-${item.path}`} className="flex items-center gap-1.5 cursor-pointer bg-white/60 px-2 py-1 rounded text-xs">
                      <input 
                        type="checkbox" 
                        checked={badgePaths.includes(item.path)}
                        onChange={(e) => setBadgePaths(e.target.checked ? [...badgePaths, item.path] : badgePaths.filter(p => p !== item.path))}
                        className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2 pb-4">
            <Button onClick={handlePublishChoices} className="bg-primary hover:bg-primary/90 gap-2">
              <Save className="w-4 h-4" /> Publier les choix (Chatbot & Badge)
            </Button>
          </div>

          <div className="bg-pink-50 p-4 rounded-xl border border-pink-200 shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-pink-700">Système de Likes (Partis)</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Permet aux citoyens de "liker" une affiche politique pour augmenter son score de visibilité.<br/>
                <strong>Mots-clés: Interactions, Gamification</strong>
              </p>
            </div>
            <Switch checked={partyLikesOn} onCheckedChange={togglePartyLikes} className="data-[state=checked]:bg-pink-500 shrink-0" />
          </div>

          <div className="bg-pink-50 p-4 rounded-xl border border-pink-200 shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-pink-700">Système de Likes (Événements)</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Permet aux citoyens d'indiquer qu'ils participent à un événement (agenda).<br/>
                <strong>Mots-clés: Agenda, Billetterie</strong>
              </p>
            </div>
            <Switch checked={eventLikesOn} onCheckedChange={toggleEventLikes} className="data-[state=checked]:bg-pink-500 shrink-0" />
          </div>

          <div className="bg-red-50 p-4 rounded-xl border border-red-200 shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="space-y-1 w-full md:max-w-[70%]">
              <Label className="text-base font-bold text-red-700">Afficher Classement Le Ring</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Permet d'afficher les classements et résultats (Saison et Rounds) dans l'arène politique Le Ring.<br/>
                <strong>Mots-clés: Ring, Classement, Résultats</strong>
              </p>
            </div>
            <Switch checked={isRingResultsEnabled} onCheckedChange={toggleRingResultsEnabled} className="data-[state=checked]:bg-red-600 shrink-0" />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
